import React, { useCallback, useEffect, useRef, useState } from "react";
import parseAccountName, { IParseAccountNameResult } from "../../../../../utils/parseAccountName";
import { useDispatch, useSelector } from "react-redux";
import { getAppUser, getUserAccounts, getUserSelectedAccountCode } from "../../../../../app/appSelectors";
import type { IAccountCode } from "../../../../../app/types/appReducer.t";
import _ from "lodash";
import { sendFacebookEventAction, setUserSelectedAccountCode } from "../../../../../app/appActions";
import { useGTMEvent } from "../../../../GoogleTagManager/googleTagManagerHooks";
import { ACCOUNT_TYPES } from "../../../../../app/appConstants";
import { GTM_EVENTS } from "../../../../GoogleTagManager/googleTagManagerConstants";
import { FB_EVENTS } from "../../../../../utils/sendFacebookEvent";

interface IParams {
    onAccountSelected?: () => void;
    onSelectAccountClick?: () => void;
    onSelectAccountClose?: ({ newValue, oldValue, source }: {
        newValue: string,
        oldValue: string,
        source?: string
    }) => void;
}

const CLOSE_SELECT_SOURCE = {
    ACCOUNT_ITEM: 'account_item',
    BACK: 'back',
    OUTSIDE: 'outside',
}

function useSelectAccount(params: IParams) {
    const { onSelectAccountClose, onSelectAccountClick, onAccountSelected } = params;
    const isSliderOpen = useRef(false);
    const [parsedAccounts, setParsedAccounts] = useState<(IParseAccountNameResult | null)[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<IParseAccountNameResult | null>(null);
    const dispatch = useDispatch();
    const accounts = useSelector(getUserAccounts);
    const disabled = accounts.length === 1;
    const selectedAccountCode: IAccountCode = useSelector(getUserSelectedAccountCode);
    const { pushGtmEvent } = useGTMEvent();
    const userProfile = useSelector(getAppUser);

    useEffect(() => {
        return () => {
            if (isSliderOpen.current) {
                onSelectAccountClose && onSelectAccountClose({
                    oldValue: selectedAccountCode,
                    newValue: selectedAccountCode,
                    source: CLOSE_SELECT_SOURCE.BACK,
                });
            }
        }
    }, [onSelectAccountClose, selectedAccountCode]);

    const handleAccountSelectAppsflyer = useCallback((accountCode) => {
        onSelectAccountClose && onSelectAccountClose({
            oldValue: selectedAccountCode,
            newValue: accountCode,
            source: CLOSE_SELECT_SOURCE.ACCOUNT_ITEM,
        })
        isSliderOpen.current = false;
    }, [onSelectAccountClose, selectedAccountCode]);

    const handleSelectAccountCancel = useCallback(() => {
        onSelectAccountClose && onSelectAccountClose({
            newValue: selectedAccountCode,
            oldValue: selectedAccountCode,
            source: CLOSE_SELECT_SOURCE.OUTSIDE,
        });
        isSliderOpen.current = false;
    }, [onSelectAccountClose, selectedAccountCode]);

    useEffect(() => {
        setParsedAccounts(accounts.map(item => parseAccountName(item.name)));
    }, [accounts]);

    useEffect(() => {
        setSelectedAccount(_.find(parsedAccounts, { accountCode: selectedAccountCode }));
    }, [parsedAccounts, selectedAccountCode]);

    const handleAccountSelect = useCallback((e: React.SyntheticEvent<HTMLButtonElement>) => {
        const value = e.currentTarget.dataset.value as IAccountCode;
        const account = _.find(parsedAccounts, { accountCode: value });
        setSelectedAccount(account);
        dispatch(setUserSelectedAccountCode(value));
        handleAccountSelectAppsflyer(value);
        if (account.type === ACCOUNT_TYPES.LIVE) {
            pushGtmEvent({ event: GTM_EVENTS.SELECT_ACCOUNT_LIVE });
            dispatch(sendFacebookEventAction({
                event: FB_EVENTS.VIEW_CONTENT,
                eventID: `select_real_account_${userProfile.uid}`,
                data: {}
            }));
        }
        onAccountSelected?.();
    }, [dispatch, handleAccountSelectAppsflyer, parsedAccounts, pushGtmEvent, onAccountSelected, userProfile]);

    const handleSelectAccountToggleClick = useCallback((event) => {
        if (disabled) {
            event.stopPropagation();
            return;
        }
        onSelectAccountClick && onSelectAccountClick();
        isSliderOpen.current = true;
    }, [disabled, onSelectAccountClick]);

    return {
        selectedAccount,
        handleSelectAccountCancel,
        handleSelectAccountToggleClick,
        disabled,
        accounts,
        handleAccountSelect,
    }
}

export default useSelectAccount;